import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const CATEGORIAS = [
  { value: 'vehiculo',    label: '🚐 Vehículo',     vida_default: 120 },
  { value: 'equipo',      label: '🔧 Equipo',       vida_default: 60 },
  { value: 'computadora', label: '💻 Computadora',  vida_default: 36 },
  { value: 'muebles',     label: '🪑 Muebles',      vida_default: 120 },
  { value: 'otro',        label: '📦 Otro',         vida_default: 60 },
]

export default function ActivosFijos() {
  const [activos, setActivos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [generando, setGenerando] = useState(false)

  useEffect(() => { cargar() }, [])
  async function cargar() {
    setLoading(true)
    const { data } = await supabase.from('activos_fijos').select('*').order('fecha_alta', { ascending: false })
    setActivos(data || [])
    setLoading(false)
  }

  async function guardar(form) {
    try {
      const payload = {
        codigo: form.codigo,
        nombre: form.nombre,
        descripcion: form.descripcion,
        categoria: form.categoria,
        fecha_alta: form.fecha_alta,
        fecha_baja: form.fecha_baja || null,
        valor_origen: Number(form.valor_origen),
        valor_residual: Number(form.valor_residual || 0),
        vida_util_meses: Number(form.vida_util_meses),
        proveedor: form.proveedor,
        factura_compra: form.factura_compra,
        notas: form.notas,
        valor_residual_actual: Number(form.valor_origen) - Number(form.amortizacion_acumulada || 0),
      }
      if (form.id) await supabase.from('activos_fijos').update(payload).eq('id', form.id)
      else await supabase.from('activos_fijos').insert({ ...payload, meses_amortizados: 0, amortizacion_acumulada: 0 })
      setModal(null)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function generarMes() {
    const mes = new Date().toISOString().slice(0, 7)
    if (!confirm(`¿Generar amortizaciones para ${mes}?\nSe creará 1 asiento por activo vigente. Si ya se generaron este mes, no duplica.`)) return
    setGenerando(true)
    try {
      const { data, error } = await supabase.rpc('generar_amortizaciones_mes', { p_mes: `${mes}-01` })
      if (error) throw error
      alert(`✓ ${data.cuotas_generadas} amortizaciones generadas`)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setGenerando(false) }
  }

  const totales = useMemo(() => {
    let valor_origen = 0, amort_acum = 0, residual = 0
    const vigentes = activos.filter(a => !a.fecha_baja)
    for (const a of vigentes) {
      valor_origen += Number(a.valor_origen)
      amort_acum += Number(a.amortizacion_acumulada)
      residual += Number(a.valor_residual_actual)
    }
    return { count: vigentes.length, valor_origen, amort_acum, residual }
  }, [activos])

  return (
    <AdminLayout>
      <div className="af-wrap">
        <div className="af-headline">
          <h1>Activos fijos</h1>
          <p>Vehículos, equipos, computadoras. Amortización lineal mensual con asiento contable automático.</p>
        </div>

        <div className="af-toolbar">
          <button className="af-btn-primary" onClick={() => setModal({
            codigo: `AF${String(activos.length + 1).padStart(3, '0')}`,
            categoria: 'vehiculo', fecha_alta: new Date().toISOString().slice(0, 10),
            fecha_baja: '', valor_origen: 0, valor_residual: 0, vida_util_meses: 120,
            amortizacion_acumulada: 0, nombre: '', descripcion: '', proveedor: '', factura_compra: '', notas: '',
          })}>+ Nuevo activo</button>
          <button className="af-btn-ghost" onClick={generarMes} disabled={generando || activos.length === 0}>
            {generando ? 'Generando…' : '⚙️ Generar amortización del mes'}
          </button>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
            Cron automático: día 1 de cada mes 02:05 AM MVD
          </div>
        </div>

        <div className="af-kpis">
          <KpiCard label="Activos vigentes" value={totales.count} color="#1A3461" />
          <KpiCard label="Valor de origen" value={'$' + fmt(totales.valor_origen)} color="#1e40af" sub="costo de adquisición" />
          <KpiCard label="Amortización acumulada" value={'$' + fmt(totales.amort_acum)} color="#dc2626" sub="ya gastado contablemente" />
          <KpiCard label="Valor residual actual" value={'$' + fmt(totales.residual)} color="#16a34a" sub="lo que queda por amortizar" />
        </div>

        {loading ? <div className="af-loading">Cargando…</div> :
         activos.length === 0 ? (
          <div className="af-empty">
            <strong>Sin activos cargados</strong>
            <p>Cuando compren el vehículo a nombre de la SAS, computadoras, mobiliario de oficina, etc., cargálo acá y se amortiza automático.</p>
          </div>
        ) : (
          <div className="af-tabla-wrap">
            <table className="af-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Categoría</th>
                  <th>Nombre</th>
                  <th>Alta</th>
                  <th style={{ textAlign: 'right' }}>Valor origen</th>
                  <th style={{ textAlign: 'right' }}>Amort. acum.</th>
                  <th style={{ textAlign: 'right' }}>Residual</th>
                  <th>Progreso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {activos.map(a => {
                  const pct = (a.meses_amortizados / a.vida_util_meses) * 100
                  const baja = !!a.fecha_baja
                  return (
                    <tr key={a.id} className={baja ? 'af-row-baja' : ''}>
                      <td><code>{a.codigo}</code></td>
                      <td>{CATEGORIAS.find(c => c.value === a.categoria)?.label || a.categoria}</td>
                      <td>
                        <div className="af-nom">{a.nombre}</div>
                        {a.descripcion && <div className="af-desc">{a.descripcion}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{a.fecha_alta}</td>
                      <td style={{ textAlign: 'right' }}>${fmt(a.valor_origen)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>${fmt(a.amortizacion_acumulada)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(a.valor_residual_actual)}</td>
                      <td>
                        <div className="af-progress">
                          <div className="af-progress-bar"><div className="af-progress-fill" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                          <div className="af-progress-label">{a.meses_amortizados}/{a.vida_util_meses} meses</div>
                        </div>
                      </td>
                      <td>{!baja && <button className="af-btn-edit" onClick={() => setModal({ ...a })}>Editar</button>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="af-info">
          <strong>📚 Cómo funciona</strong>
          <ul>
            <li><b>Línea recta:</b> cuota mensual = (valor origen − valor residual) / vida útil en meses</li>
            <li><b>Vida útil típica:</b> Vehículos 10 años (120 meses), Equipos 5 años (60), Computadoras 3 años (36), Muebles 10 años (120)</li>
            <li><b>Asiento mensual:</b> DEBE 5.4.04 Amortizaciones del ejercicio / HABER 1.2.02 Amortizaciones acumuladas</li>
            <li><b>Cron automático:</b> día 1 de cada mes a las 02:05 AM MVD. Idempotente (no duplica si ya se corrió).</li>
            <li><b>Cuando se da de baja:</b> setear fecha_baja → deja de amortizarse</li>
          </ul>
        </div>
      </div>

      {modal && <ModalActivo initial={modal} onClose={() => setModal(null)} onSave={guardar} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function ModalActivo({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial)
  const [proveedoresLista, setProveedoresLista] = useState([])
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value })

  // Cargar lista de proveedores activos para autocomplete del input
  useEffect(() => {
    supabase.from('proveedores')
      .select('id, nombre, categoria')
      .eq('activo', true)
      .order('nombre')
      .then(({ data }) => setProveedoresLista(data || []))
  }, [])

  const cuotaMensual = useMemo(() => {
    if (!f.valor_origen || !f.vida_util_meses) return 0
    return (Number(f.valor_origen) - Number(f.valor_residual || 0)) / Number(f.vida_util_meses)
  }, [f.valor_origen, f.valor_residual, f.vida_util_meses])

  return (
    <div className="af-modal-bg" onClick={onClose}>
      <div className="af-modal" onClick={e => e.stopPropagation()}>
        <button className="af-modal-close" onClick={onClose}>×</button>
        <h2>{f.id ? 'Editar activo' : 'Nuevo activo fijo'}</h2>
        <form onSubmit={e => { e.preventDefault(); onSave(f) }}>
          <div className="af-form-row">
            <Field label="Código *"><input type="text" value={f.codigo} onChange={upd('codigo')} required /></Field>
            <Field label="Categoría *">
              <select value={f.categoria} onChange={e => {
                const cat = CATEGORIAS.find(c => c.value === e.target.value)
                setF({ ...f, categoria: e.target.value, vida_util_meses: cat?.vida_default || f.vida_util_meses })
              }}>
                {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Nombre *"><input type="text" value={f.nombre} onChange={upd('nombre')} required placeholder="Ej: Note 2024 patente ABC1234" /></Field>
          <Field label="Descripción"><textarea rows={2} value={f.descripcion || ''} onChange={upd('descripcion')} /></Field>

          <h3 className="af-section">💰 Valores</h3>
          <div className="af-form-row">
            <Field label="Valor de origen *"><input type="number" step="0.01" value={f.valor_origen} onChange={upd('valor_origen')} required /></Field>
            <Field label="Valor residual estimado"><input type="number" step="0.01" value={f.valor_residual || 0} onChange={upd('valor_residual')} /></Field>
          </div>
          <div className="af-form-row">
            <Field label="Vida útil (meses) *"><input type="number" value={f.vida_util_meses} onChange={upd('vida_util_meses')} required /></Field>
            <Field label="Amortización ya acumulada"><input type="number" step="0.01" value={f.amortizacion_acumulada || 0} onChange={upd('amortizacion_acumulada')} /></Field>
          </div>

          <div className="af-calc">
            <div><span>Cuota mensual:</span><b>${fmt(cuotaMensual)}</b></div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Fórmula: (valor origen − valor residual) / vida útil en meses</div>
          </div>

          <h3 className="af-section">📅 Fechas</h3>
          <div className="af-form-row">
            <Field label="Fecha de alta *"><input type="date" value={f.fecha_alta} onChange={upd('fecha_alta')} required /></Field>
            <Field label="Fecha de baja (si se vendió/dio de baja)"><input type="date" value={f.fecha_baja || ''} onChange={upd('fecha_baja')} /></Field>
          </div>

          <h3 className="af-section">📋 Documentación</h3>
          <div className="af-form-row">
            <Field label="Proveedor">
              <input type="text" list="af-proveedores-list" value={f.proveedor || ''} onChange={upd('proveedor')} placeholder="Escribí o elegí de la lista" />
              <datalist id="af-proveedores-list">
                {proveedoresLista.map(p => (
                  <option key={p.id} value={p.nombre}>{p.categoria ? `· ${p.categoria}` : ''}</option>
                ))}
              </datalist>
            </Field>
            <Field label="Factura de compra"><input type="text" value={f.factura_compra || ''} onChange={upd('factura_compra')} /></Field>
          </div>
          <Field label="Notas"><textarea rows={2} value={f.notas || ''} onChange={upd('notas')} /></Field>

          <div className="af-modal-actions">
            <button type="button" className="af-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="af-btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children }) { return <label className="af-field"><span>{label}</span>{children}</label> }
function KpiCard({ label, value, sub, color }) {
  return <div className="af-kpi"><div className="af-kpi-l">{label}</div><div className="af-kpi-v" style={{ color }}>{value}</div>{sub && <div className="af-kpi-s">{sub}</div>}</div>
}

const CSS = `
.af-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.af-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.af-headline p { font-size: 13px; color: #64748b; margin: 0 0 16px }
.af-toolbar { display: flex; gap: 10px; align-items: center; margin-bottom: 14px; flex-wrap: wrap }
.af-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 16px }
.af-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.af-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.af-kpi-v { font-size: 22px; font-weight: 900 }
.af-kpi-s { font-size: 11px; color: #64748b }
.af-loading, .af-empty { padding: 40px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.af-empty strong { display: block; color: #1A3461; font-size: 15px; margin-bottom: 6px }
.af-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.af-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.af-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 8px 14px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit }
.af-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit }
.af-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.af-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 1100px }
.af-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.af-tabla td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.af-tabla code { background: #e0e7ff; color: #3730a3; padding: 1px 6px; border-radius: 3px; font-size: 11.5px }
.af-row-baja { opacity: .5 }
.af-nom { font-weight: 700 }
.af-desc { font-size: 11px; color: #64748b }
.af-progress { min-width: 140px }
.af-progress-bar { height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden }
.af-progress-fill { height: 100%; background: linear-gradient(90deg, #00A8B5, #1A3461); border-radius: 999px }
.af-progress-label { font-size: 10.5px; color: #64748b; margin-top: 2px }
.af-info { margin-top: 18px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.af-info strong { font-size: 13px }
.af-info ul { margin: 8px 0; padding-left: 22px }
.af-info b { color: #1e40af }
.af-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.af-modal { background: #fff; border-radius: 14px; max-width: 640px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.af-modal h2 { margin: 0 0 14px; font-size: 19px; color: #1A3461; font-weight: 900 }
.af-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.af-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0 }
.af-section { font-size: 13px; font-weight: 800; color: #475569; margin: 16px 0 8px; padding-top: 12px; border-top: 1px solid #e2e8f0 }
.af-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.af-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.af-field span { font-size: 12px; font-weight: 700; color: #475569 }
.af-field input, .af-field select, .af-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
.af-calc { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; margin: 8px 0 12px; font-size: 13px }
.af-calc div { display: flex; justify-content: space-between }
.af-calc span { color: #64748b }
.af-calc b { color: #1A3461; font-size: 16px }
`
